import { Alert, Card, Flex, Spin, Table } from 'antd';
import { ErrorDisplay } from 'components/ErrorDisplay';
import { Text, Title } from 'components/mvp-typography';
import { PrintableRootPage } from 'components/page/page-2';
import { formatDate } from 'date-fns';
import { CRxPlotlyComponentReference } from 'features/patient/patient-summary/components/crx-plotly-import';
import { AssessmentType } from 'lib/documents/patient-assessment/patient-assessment';
import {
  useAggregateDataQuery,
  useClinicAssessmentBalance
} from 'lib/hooks/tanstack/use-clinicAssessmentBalance';
import { useHasPaymentMethod } from 'lib/hooks/tanstack/use-getProductPrice';
import { useClinicianContext } from 'lib/hooks/use-resolveCurrentClinicInfo';
import { resolveError } from 'lib/utils/formatters/error/resolve-error';
import { InfinityIcon } from 'lucide-react';
import { useMemo } from 'react';
import { PlotParams } from 'react-plotly.js';
import { useLocation } from 'react-router-dom';
import { theme } from 'styles/colors';
import { TopUpBalanceButton } from './_components/button-balance-topup';
import { InternationalNumberFormatter } from './formatNumber';

function useProductUsageTrendlineData(currentClinic: string) {
  const aggregateUseQuery = useAggregateDataQuery(currentClinic, 'month');

  const yearlyUsageChartData = useMemo(() => {
    if (aggregateUseQuery.isLoading || !aggregateUseQuery.data?.data) {
      return { x: [], add: [], pending: [], cancelled: [], consumed: [] };
    }

    const data = aggregateUseQuery.data.data;
    const sortedKeys = Object.keys(data).sort();

    return {
      x: sortedKeys.map(key => formatDate(new Date(key), 'MMM yyyy')),
      add: sortedKeys.map(key => data[key].add),
      pending: sortedKeys.map(key => data[key].pending),
      cancelled: sortedKeys.map(key => data[key].cancelled),
      consumed: sortedKeys.map(key => data[key].consumed)
    };
  }, [aggregateUseQuery.data, aggregateUseQuery.isLoading]);

  const productUsageData: Pick<PlotParams, 'data' | 'layout' | 'config'> =
    useMemo(() => {
      return {
        data: [
          {
            x: yearlyUsageChartData.x,
            y: yearlyUsageChartData.add,
            type: 'bar',
            name: 'Purchased',
            marker: { color: theme.gauge.orange }
          },
          {
            x: yearlyUsageChartData.x,
            y: yearlyUsageChartData.consumed,
            type: 'bar',
            name: 'Used',
            marker: { color: theme.gauge.green }
          }
        ],
        layout: {
          title: {
            text: 'Usage Over Time'
          },
          xaxis: {
            title: {
              text: 'Month'
            },
            tickangle: -45,
            type: 'category'
          },
          yaxis: {
            title: {
              text: 'Usage Count'
            },
            rangemode: 'tozero',
            autorange: true
          },
          barmode: 'group',
          hovermode: 'closest',
          autosize: true,
          showlegend: true,
          legend: {
            orientation: 'h',
            yanchor: 'bottom',
            y: 1.02,
            xanchor: 'right',
            x: 1
          }
        },
        config: {
          displayModeBar: false,
          responsive: true,
          editable: false
        }
      };
    }, [yearlyUsageChartData]);

  return productUsageData;
}

export function BillingOrganisationUsagePage() {
  const location = useLocation();

  const clinicianContext = useClinicianContext();
  /**
   * Check if the organisation has a payment method set up
   */
  const hasPaymentMethodQuery = useHasPaymentMethod(
    clinicianContext.currentClinic
  );

  // Prepare aggregate data for the Table and Chart
  // Assuming the API returns history for the last year when period is 'month'
  const aggregateUseQuery = useAggregateDataQuery(
    clinicianContext.currentClinic,
    'month'
  );

  const aggregateUseDataSource = useMemo(() => {
    if (aggregateUseQuery.isLoading || !aggregateUseQuery.data?.data) return [];

    // The data is an object with ISO date keys. Get the most recent one.
    const keys = Object.keys(aggregateUseQuery.data.data).sort();
    const latestKey = keys[keys.length - 1];
    const latestData = latestKey
      ? aggregateUseQuery.data.data[latestKey]
      : null;

    if (!latestData) return [];

    return [
      {
        key: '1',
        metric: 'Added',
        quantity: latestData.add
      },
      {
        key: '2',
        metric: 'Used',
        quantity: latestData.consumed
      },
      {
        key: '4',
        metric: 'Cancelled',
        quantity: latestData.cancelled
      }
    ];
  }, [aggregateUseQuery.data, aggregateUseQuery.isLoading]);

  const productUsageData = useProductUsageTrendlineData(
    clinicianContext.currentClinic
  );

  if (aggregateUseQuery.isLoading) {
    return <Spin />;
  }

  const aggregateUseColumns = [
    {
      title: 'Metric',
      dataIndex: 'metric',
      key: 'metric'
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity'
    }
  ];
  const hasPaymentMethod =
    hasPaymentMethodQuery.data?.data.hasPaymentMethod === true;
  return (
    <PrintableRootPage title="Product usage">
      <Flex gap={16} vertical>
        {/* Overview Section */}
        <Flex justify="space-between" align="center">
          <Title level={2}>Overview</Title>
          <TopUpBalanceButton
            hasPaymentMethod={hasPaymentMethod}
            pathname={location.pathname}
          />
        </Flex>

        <Alert
          type="info"
          showIcon
          message="Billing & Top-ups"
          description={
            <Text>
              ConcussionRx operates on a balance model for test administration.
              You can purchase tests in bulk for a lower unit price, or use the{' '}
              <b>Pay-Per-Use</b> system to purchase a single test on-demand.
              Tests are deducted from your balance as you assign tests to
              test-takers.
            </Text>
          }
        />

        <Flex gap={16}>
          <WrappedDisplayBalanceMetric
            balanceLabel="Baseline test"
            balanceType="baseline"
          />
          <WrappedDisplayBalanceMetric
            balanceLabel="Post-Injury test"
            balanceType="post-injury"
          />
        </Flex>

        {/* Usage over the last 30 days Section */}
        <div>
          <h3>Usage over the last year</h3>
          <Card>
            <CRxPlotlyComponentReference
              style={{ width: '100%' }}
              data={productUsageData.data}
              layout={productUsageData.layout}
              config={productUsageData.config}
            />
          </Card>
        </div>

        {/* Aggregate use this month Section */}
        <div>
          <h3>Aggregate use this month</h3>
          <Card>
            <Table
              dataSource={aggregateUseDataSource}
              columns={aggregateUseColumns}
              pagination={false}
            />
          </Card>
        </div>
      </Flex>
    </PrintableRootPage>
  );
}

function WrappedDisplayBalanceMetric(props: {
  balanceLabel: string;
  balanceType: AssessmentType;
}) {
  const data = useClinicAssessmentBalance(props.balanceType);
  if (data.isLoading) {
    return <Spin />;
  }

  if (data.isError) {
    return <ErrorDisplay error={resolveError(data.error)} />;
  }
  return (
    <DisplayBalanceMetric
      balanceLabel={props.balanceLabel}
      balanceType={props.balanceType.toUpperCase()}
      mainQuantity={data.data?.data.available}
      pendingQuantity={data.data?.data.allocated}
      isInfinite={data.data?.data.isInfinite}
    />
  );
}

function DisplayBalanceMetric(props: {
  balanceLabel: string;
  balanceType: string;
  mainQuantity: number;
  pendingQuantity: number;
  isInfinite: boolean;
}) {
  const formattedMain = props.isInfinite ? (
    <InfinityIcon size={64} />
  ) : (
    InternationalNumberFormatter.format(props.mainQuantity)
  );
  const formattedPending = InternationalNumberFormatter.format(
    props.pendingQuantity
  );

  return (
    <Card style={{ flex: 1 }}>
      <Text style={{ fontWeight: 'bold' }}>{props.balanceLabel}</Text>
      <Flex vertical align="center">
        <Title>{formattedMain}</Title>
        <p>({formattedPending} pending)</p>
      </Flex>

      {props.mainQuantity === 0 && !props.isInfinite && (
        <Alert
          showIcon
          type="warning"
          message={'Zero balance'}
          description={
            'You will not be able to administer tests of this type until you purchase at least one.'
          }
        />
      )}
    </Card>
  );
}
